// User Types
//
// Backend `role` is one of the supported SmartCHG role tiers.
// (CLAUDE.md §1). SmartCHG-aware paths operate on canonical role values
// plus explicit permission checks.
export const SMARTCHG_ROLES = [
  'super_admin',
  'allocation_admin',
  'finance_admin',
  'operations_admin',
  'finance_user',
  'operations_user',
  'smart_insights_v3',
] as const;

export type SmartCHGRole = (typeof SMARTCHG_ROLES)[number];

export const DEFAULT_SMARTCHG_ROLE: SmartCHGRole = 'finance_user';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  /** Canonical SmartCHG role tier. */
  role: SmartCHGRole;
  /** Legacy multi-tenant field. SmartCHG is single-tenant (CLAUDE.md §2 #4) so
   *  AuthContext no longer populates this; left optional/null for older profile
   *  payload compatibility. */
  organizationId?: string | null;
  department?: string;
  /** Microsoft Graph officeLocation code, when available. */
  officeLocation?: string;
  /** Permission strings (e.g. "smart_insights_bot.access"). Loaded from /api/users/profile. */
  permissions?: string[];
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string;
  gmailId?: string;
  slackId?: string;
  teamId?: string;
  /** Workday / Employee ID synced from Microsoft Entra ID via Graph API */
  employeeId?: string;
  /** SmartCHG profile preferences (PATCH /api/users/profile). */
  displayName?: string;
  preferredLanguage?: string;
  defaultDatasourceId?: string;
  /** Onboarding flag returned by GET /api/users/profile. */
  isNewUser?: boolean;
}

// Notification Types
export interface Notification {
  id: string;
  title: string;
  message: string;
  category: 'DEVICE_ASSIGNMENT' | 'DEVICE_CREATION' | 'DEVICE_UPDATE' | 'MAINTENANCE_SCHEDULE' | 'MAINTENANCE_REMINDER' | 'MAINTENANCE_ASSIGNMENT' | 'DEVICE_OFFLINE' | 'DEVICE_ONLINE' | 'TEMPERATURE_ALERT' | 'BATTERY_LOW' | 'RULE_TRIGGERED' | 'RULE_CREATED' | 'SYSTEM_UPDATE' | 'SECURITY_ALERT' | 'PERFORMANCE_ALERT' | 'SAFETY_ALERT' | 'CUSTOM';
  read: boolean;
  deviceId?: string | null;
  ruleId?: string | null;
  userId: string;
  organizationId: string;
  createdAt: string;
  metadata?: Record<string, string> | null;
  
  // Additional asset metadata fields
  deviceName?: string | null;
  deviceType?: string | null;
  deviceLocation?: string | null;
  deviceStatus?: string | null;
  maintenanceRulesCount?: number | null;
  safetyRulesCount?: number | null;
  totalRulesCount?: number | null;
  deviceManufacturer?: string | null;
  deviceModel?: string | null;
}

// Notification Event Types
export interface NotificationEvent {
  type: 'DEVICE_ASSIGNMENT' | 'DEVICE_CREATION' | 'DEVICE_UPDATE' | 'MAINTENANCE_SCHEDULE' | 'MAINTENANCE_REMINDER' | 'MAINTENANCE_ASSIGNMENT' | 'DEVICE_OFFLINE' | 'DEVICE_ONLINE' | 'TEMPERATURE_ALERT' | 'BATTERY_LOW' | 'RULE_TRIGGERED' | 'RULE_CREATED' | 'SYSTEM_UPDATE' | 'SECURITY_ALERT' | 'PERFORMANCE_ALERT' | 'SAFETY_ALERT' | 'CUSTOM';
  deviceId?: string;
  deviceName?: string;
  userId: string;
  data?: Record<string, any>;
}

// Notification Template Types
export interface NotificationTemplate {
  id: string;
  name: string;
  type: 'DEVICE_ASSIGNMENT' | 'DEVICE_CREATION' | 'DEVICE_UPDATE' | 'MAINTENANCE_SCHEDULE' | 'MAINTENANCE_REMINDER' | 'MAINTENANCE_ASSIGNMENT' | 'DEVICE_OFFLINE' | 'DEVICE_ONLINE' | 'TEMPERATURE_ALERT' | 'BATTERY_LOW' | 'RULE_TRIGGERED' | 'RULE_CREATED' | 'SYSTEM_UPDATE' | 'SECURITY_ALERT' | 'PERFORMANCE_ALERT' | 'SAFETY_ALERT' | 'CUSTOM';
  titleTemplate: string;
  messageTemplate: string;
  notificationType: Notification['category'];
  active: boolean;
  organizationId: string;
  createdBy?: string;
  createdAt: string;
  updatedAt?: string;
  variables?: Record<string, string> | null;
  description?: string;
}

export interface NotificationTemplateRequest {
  name: string;
  type: NotificationTemplate['type'];
  titleTemplate: string;
  messageTemplate: string;
  notificationType: NotificationTemplate['notificationType'];
  active?: boolean;
  description?: string;
  variables?: Record<string, string>;
}

export interface ProcessedTemplate {
  title: string;
  message: string;
  type: NotificationTemplate['notificationType'];
}
